import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, Teleport as _Teleport } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "flex flex-col gap-3 animate-pulse",
  "data-testid": "post-station-details-skeleton"
}
const _hoisted_2 = /*#__PURE__*/_createElementVNode("div", { class: "h-5 w-40 rounded bg-slate-200" }, null, -1)
const _hoisted_3 = /*#__PURE__*/_createElementVNode("div", { class: "h-20 w-full rounded-lg bg-slate-200" }, null, -1)
const _hoisted_4 = [
  _hoisted_2,
  _hoisted_3
]
const _hoisted_5 = {
  key: 1,
  class: "flex flex-col gap-3 text-left",
  "data-testid": "post-station-details-section"
}
const _hoisted_6 = {
  key: 0,
  class: "text-lg font-bold text-left"
}
const _hoisted_7 = {
  key: 0,
  class: "flex items-start gap-2 px-2 py-1 rounded-md border border-primary30 bg-primary5 text-sm text-textPrimary",
  "data-testid": "post-station-details-lock-banner"
}
const _hoisted_8 = {
  class: "grid grid-cols-3 md:grid-cols-6 gap-x-3 gap-y-4 rounded-lg py-3 px-3 md:px-6",
  style: {"border-radius":"8px","border":"1px solid var(--Neutral-LightGray-30, #d8d8e2)","background":"var(--White, #fff)"}
}
const _hoisted_9 = ["data-testid"]
const _hoisted_10 = ["title"]
const _hoisted_11 = {
  class: "flex flex-col items-center gap-2 min-w-0",
  "data-testid": "post-station-details-status"
}
const _hoisted_12 = /*#__PURE__*/_createElementVNode("span", { class: "text-xs md:text-sm font-bold text-center w-full truncate" }, "Status", -1)

import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { mdiInformation, mdiOpenInNew } from '@mdi/js'

import SVGIcon from '@/components/SVGIcon.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import Network from '@/Network'
import type { PostStationStationEvaluationResponse } from '@/Network/Types/Responses/PostStationResponses'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { AuthorizationPermissions, AuthorizationResourcesAndSubjects } from '@/types/authorization'
import PostStationRevertedCallout from '@/modules/company/Dashboard/components/PostStationRevertedCallout.vue'
import ComplianceIcon from '@/modules/company/PostStation/components/ComplianceIcon.vue'
import PostStationDetailSidebar from '@/modules/company/PostStation/components/PostStationDetailSidebar.vue'
import PostStationStatusBadge from '@/modules/company/PostStation/components/PostStationStatusBadge.vue'
import {
  COMPLIANCE_STATE_TITLES,
  POST_STATION_LOCK_BANNER_TEXT,
  registrationCellTitle,
  resolveRegistrationCellStatus
} from '@/modules/company/PostStation/constants'
import { mapEvaluationToRow } from '@/modules/company/PostStation/mappers'
import { PostStationStatus } from '@/modules/company/PostStation/types'
import type {
  PostStationCompliance,
  PostStationDomainDetail
} from '@/modules/company/PostStation/types'


export default /*#__PURE__*/_defineComponent({
  __name: 'PostStationDetailsSection',
  props: {
    stationId: {},
    scheduleId: {},
    evaluation: { default: undefined },
    showTitle: { type: Boolean, default: true }
  },
  emits: ["sidebarToggle"],
  setup(__props: any, { emit }) {

const props = __props;





// Rótulos curtos das colunas do card (o card tem 6 colunas dentro do modal, não
// cabe o nome completo). Local de propósito: `constants.ts` só tem os rótulos
// longos (DOMAIN_LABELS) e as labels do grid do painel são chaveadas por coluna.
const SHORT_DOMAIN_LABELS: Record<keyof PostStationCompliance, string> = {
  registration: 'Reg. Plantão',
  qualification: 'Doc. Qual.',
  repass: 'Repasse',
  exception: 'Excep.',
  // "Pol. Pgto." e não "Pgto.": o domínio é a POLÍTICA de pagamento
  // (`DOMAIN_LABELS.payment`), e o rótulo curto anterior se lia como status de
  // pagamento do plantão.
  payment: 'Pol. Pgto.'
}

const { flags } = useFeatureFlags()
const permissionsStore = usePermissionsStore()

// Mesmo padrão de `checkSchedulePermission` do stationDetailsModal: a permissão
// de leitura do status é concedida no escopo da escala do plantão.
const canReadPostStation = computed(() => {
  if (!flags.enabledPostStation || !props.scheduleId) return false
  return permissionsStore.check({
    permission: AuthorizationPermissions.PostStationStationStatusReading,
    resource: {
      objectType: AuthorizationResourcesAndSubjects.Schedule,
      objectId: props.scheduleId
    }
  })
})

// `lastReversion` vem junto na resposta e é o que o callout do RF-23 (FE-5) vai
// consumir — por isso guardamos a resposta inteira, não só a row mapeada.
const fetchedEvaluation = ref<PostStationStationEvaluationResponse | null>(null)
const isLoading = ref(false)
const isSidebarOpen = ref(false)

const setSidebarOpen = (value: boolean) => {
  isSidebarOpen.value = value
  emit('sidebarToggle', value)
}

const openSidebar = () => setSidebarOpen(true)

// Fechar o modal desmonta o corpo dele (e esta seção com a sidebar). Sem avisar,
// o pai guardaria a flag ligada e reabriria o modal deslizado sem sidebar.
onBeforeUnmount(() => {
  if (isSidebarOpen.value) setSidebarOpen(false)
})

// Guard de corrida: só a request mais recente escreve no estado (o modal é
// reaproveitado entre plantões e o stationId troca sem desmontar).
let fetchSeq = 0

const fetchEvaluation = async (stationId: number) => {
  const seq = ++fetchSeq
  isLoading.value = true
  try {
    // `null` = 404 = plantão sem avaliação de pós-plantão (estado esperado).
    const response = await Network.postStation.getStationEvaluation(stationId)
    if (seq !== fetchSeq) return
    fetchedEvaluation.value = response
  } catch (e) {
    if (seq !== fetchSeq) return
    fetchedEvaluation.value = null
    // Falha real de backend: o card não aparece (não há o que mostrar), mas o
    // erro não pode desaparecer silenciosamente.
    console.error('Falha ao buscar a avaliação de pós-plantão do plantão', e)
  } finally {
    if (seq === fetchSeq) isLoading.value = false
  }
}

// `canReadPostStation` entra como fonte porque as permissões chegam por request:
// o primeiro run pode ver `false` e liberar depois que o store carrega.
watch(
  [() => props.stationId, canReadPostStation],
  ([stationId, canRead]) => {
    fetchedEvaluation.value = null
    // Trocar de plantão desmonta a sidebar: avisa o modal para voltar à posição.
    if (isSidebarOpen.value) setSidebarOpen(false)
    // Avaliação vinda por prop: o pai já buscou, não repetir a request.
    if (props.evaluation !== undefined) {
      isLoading.value = false
      return
    }
    if (!canRead || !stationId) {
      fetchSeq += 1
      isLoading.value = false
      return
    }
    fetchEvaluation(stationId)
  },
  { immediate: true }
)

// Fonte única para o resto do componente: a prop quando fornecida, senão o que
// o fetch próprio trouxe.
const resolvedEvaluation = computed(() =>
  props.evaluation !== undefined ? props.evaluation : fetchedEvaluation.value
)

const row = computed(() =>
  resolvedEvaluation.value ? mapEvaluationToRow(resolvedEvaluation.value) : null
)

const isApproved = computed(() => row.value?.status === PostStationStatus.APPROVED)

const isReverted = computed(() => row.value?.status === PostStationStatus.REVERTED)

const lastReversion = computed(() => resolvedEvaluation.value?.lastReversion ?? null)

// Offset ISO do plantão (`-03:00`), vindo de `stations.timezone` no backend.
const stationTimezone = computed(() => resolvedEvaluation.value?.timezone ?? null)

/**
 * F4 — o estado de quatro valores é EXCLUSIVO do domínio de registro, e a
 * decisão de pintá-lo é a mesma da célula do grid: `resolveRegistrationCellStatus`
 * devolve `null` para escala sem política vigente
 * (`registrationPolicyInEffect === false`), campo ausente (backend anterior à
 * T9) e valor desconhecido — e aí o card volta à bolinha de conformidade, que
 * já diz "Não avaliado". Sem a `row` não há o que resolver.
 */
const registrationStatusFor = (domain: PostStationDomainDetail) => {
  if (domain.key !== 'registration' || !row.value) return null
  return resolveRegistrationCellStatus(row.value)
}

const domainTitle = (domain: PostStationDomainDetail) => {
  if (domain.key === 'registration' && row.value) {
    return `${domain.label} — ${registrationCellTitle(row.value, domain.state)}`
  }
  return `${domain.label} — ${COMPLIANCE_STATE_TITLES[domain.state]}`
}

return (_ctx: any,_cache: any) => {
  return (canReadPostStation.value)
    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
        (isLoading.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_1, _hoisted_4))
          : (row.value)
            ? (_openBlock(), _createElementBlock("section", _hoisted_5, [
                _createElementVNode("div", {
                  class: _normalizeClass(["flex items-center gap-2 flex-wrap", _ctx.showTitle ? 'justify-between' : 'justify-end'])
                }, [
                  (_ctx.showTitle)
                    ? (_openBlock(), _createElementBlock("h2", _hoisted_6, "Pós-Plantão"))
                    : _createCommentVNode("", true),
                  _createVNode(OutlineButton, {
                    class: "gap-1",
                    height: "32px",
                    width: "152px",
                    "data-testid": "post-station-details-open-sidebar-button",
                    onClick: openSidebar
                  }, {
                    default: _withCtx(() => [
                      _createVNode(SVGIcon, {
                        path: _unref(mdiOpenInNew),
                        class: "w-4 h-4"
                      }, null, 8, ["path"]),
                      _createTextVNode(" Ver detalhes ")
                    ]),
                    _: 1
                  })
                ], 2),
                (isApproved.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                      _createVNode(SVGIcon, {
                        path: _unref(mdiInformation),
                        class: "w-4 h-4 mt-0.5 shrink-0 text-primary30"
                      }, null, 8, ["path"]),
                      _createElementVNode("span", null, _toDisplayString(_unref(POST_STATION_LOCK_BANNER_TEXT)), 1)
                    ]))
                  : _createCommentVNode("", true),
                (isReverted.value && lastReversion.value)
                  ? (_openBlock(), _createBlock(PostStationRevertedCallout, {
                      key: 1,
                      reversion: lastReversion.value,
                      timezone: stationTimezone.value
                    }, null, 8, ["reversion", "timezone"]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_8, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(row.value.detail.domains, (domain) => {
                    return (_openBlock(), _createElementBlock("div", {
                      key: domain.key,
                      class: "flex flex-col items-center gap-2 min-w-0",
                      "data-testid": `post-station-details-domain-${domain.key}`
                    }, [
                      _createElementVNode("span", {
                        class: "text-xs md:text-sm font-bold text-center w-full truncate",
                        title: domainTitle(domain)
                      }, _toDisplayString(SHORT_DOMAIN_LABELS[domain.key]), 9, _hoisted_10),
                      _createVNode(ComplianceIcon, {
                        state: domain.state,
                        "domain-label": domain.label,
                        "registration-status": registrationStatusFor(domain)
                      }, null, 8, ["state", "domain-label", "registration-status"])
                    ], 8, _hoisted_9))
                  }), 128)),
                  _createElementVNode("div", _hoisted_11, [
                    _hoisted_12,
                    _createVNode(PostStationStatusBadge, {
                      status: row.value.status
                    }, null, 8, ["status"])
                  ])
                ])
              ]))
            : _createCommentVNode("", true),
        (row.value)
          ? (_openBlock(), _createBlock(_Teleport, {
              key: 2,
              to: "#app"
            }, [
              _createVNode(PostStationDetailSidebar, {
                "model-value": isSidebarOpen.value,
                row: row.value,
                "evaluation-id": row.value.evaluationId,
                "onUpdate:modelValue": setSidebarOpen
              }, null, 8, ["model-value", "row", "evaluation-id"])
            ]))
          : _createCommentVNode("", true)
      ], 64))
    : _createCommentVNode("", true)
}
}

})